import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttendanceRecord } from '@tailoring-crm/shared';
import { useMarkLeave } from '../../lib/attendance/use-attendance.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * They are not coming in — and why, if anybody knows.
 *
 * The reason is OPTIONAL, and that is the whole design of this dialog. A shop told "her sister's
 * wedding in Erode" should be able to keep it, because months later that is the only thing an
 * absence is worth anything for; a shop whose tailor simply did not arrive must not be made to
 * invent something before the day can be recorded. A required field here would be filled with
 * "personal" a hundred times and the record would say nothing.
 *
 * It writes through the correction endpoint rather than through a third button beside check-in
 * and check-out, because leave is a statement about a NAMED DAY — including next Tuesday — and
 * the two clock buttons only ever mean today.
 */
export function MarkLeaveDialog({
  record,
  onOpenChange,
}: {
  /** Whose day is being marked, or null when the dialog is closed. */
  record: AttendanceRecord | null;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const markLeave = useMarkLeave();
  const [reason, setReason] = useState('');

  const close = (): void => {
    setReason('');
    onOpenChange(false);
  };

  const submit = (): void => {
    if (!record) return;
    void (async () => {
      try {
        await markLeave.mutateAsync({
          staffUserId: record.staffUserId,
          day: record.day,
          reason: reason.trim() || null,
        });
        toast({ title: t('attendance.leaveMarked'), variant: 'success' });
        close();
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <Dialog open={record !== null} onOpenChange={(open) => (open ? undefined : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('attendance.leave.title', { name: record?.staffName ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('attendance.leave.hint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="attendance-leave-reason">{t('attendance.leave.reasonLabel')}</Label>
          <Input
            id="attendance-leave-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('attendance.leave.reasonPlaceholder')}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button disabled={markLeave.isPending} onClick={submit}>
            {t('attendance.leave.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MarkLeaveDialog;
